// englishContent.js
// Grade9Ready — Workbook-aligned English content index.
// -----------------------------------------------------
// This file defines the English course structure, adapts the authored
// workbook content files into the topic-page schema, appends GCSE modules,
// and decorates topics with examiner-bank insights.

import { englishContent1 } from './englishcontent1';
import { englishContent2 } from './englishcontent2';
import { englishContent3 } from './englishcontent3';
import { englishContent4 } from './englishcontent4';
import { englishContent5 } from './englishcontent5';
import { englishContent6 } from './englishcontent6';
import { englishContent7 } from './englishcontent7';
import { englishGCSESections } from './englishGCSE';
import { englishGCSESections2 } from './englishGCSE2';
import { attachExaminerInsights } from './examinerBank';

// ============================================================
// Base topic factory
// ============================================================

const makeTopic = ({
  id,
  slug,
  title,
  summary,
  yearGroup = '7-11'
}) => ({
  id,
  slug,
  title,
  summary,
  yearGroup,
  explainer: {
    intro: `${title} is part of the Grade 9 Ready English course. This topic will be expanded into a full lesson aligned to the workbook.`,
    keyPoints: [
      `Core knowledge for ${title}.`,
      `Workbook-aligned teaching content to be added.`,
      `Examples and model answers to be added.`,
      `Practice and application tasks to be added.`
    ],
    rule: {
      name: title,
      description: `Key guidance for ${title} will be added here.`
    },
    examples: [
      {
        label: 'Example 1',
        text: `Example content for ${title}.`
      },
      {
        label: 'Example 2',
        text: `Additional example content for ${title}.`
      }
    ]
  },
  interactive: [
    {
      q: `Starter check for ${title}: this interactive content will be added next.`,
      type: 'mcq',
      options: ['Option A', 'Option B', 'Option C', 'Option D'],
      answer: 0,
      feedback: `Interactive lesson content for ${title} will be built here.`
    }
  ],
  workedExample: {
    prompt: `Worked example for ${title} will be added here.`,
    tiers: [
      {
        label: 'Weak',
        text: `Basic response placeholder for ${title}.`,
        critique: 'This placeholder will be replaced with a real model.'
      },
      {
        label: 'Good',
        text: `Improved response placeholder for ${title}.`,
        critique: 'This placeholder will be replaced with a real model.'
      },
      {
        label: 'Excellent',
        text: `Top-band response placeholder for ${title}.`,
        critique: 'This placeholder will be replaced with a real model.'
      }
    ]
  },
  workedExamples: [
    {
      label: 'Example 1 — Modelled answer',
      prompt: `Worked example for ${title} will be added here.`,
      tiers: [
        {
          label: 'Weak',
          text: `Basic response placeholder for ${title}.`,
          critique: 'This placeholder will be replaced with a real model.'
        },
        {
          label: 'Good',
          text: `Improved response placeholder for ${title}.`,
          critique: 'This placeholder will be replaced with a real model.'
        },
        {
          label: 'Excellent',
          text: `Top-band response placeholder for ${title}.`,
          critique: 'This placeholder will be replaced with a real model.'
        }
      ]
    }
  ],
  practice: [
    {
      q: `Practice question for ${title}`,
      type: 'mcq',
      options: ['Option A', 'Option B', 'Option C', 'Option D'],
      answer: 0
    }
  ],
  application: {
    prompt: `Application task for ${title} will be added here.`,
    guidance: `Workbook-aligned application guidance for ${title}.`,
    wordTarget: '100-150',
    techniques: [`Technique focus for ${title}`]
  },
  improvement: {
    lead: `Improvement task for ${title} will be added here.`
  }
});

// ============================================================
// Base English section structure
// ============================================================
// This is the stable navigation/index structure.
// The rich workbook-authored content is merged below by section/topic slug.
// ============================================================

export const englishSections = [
  {
    id: 1,
    title: 'Understanding Fiction & Non-Fiction',
    slug: 'understanding-fiction-and-non-fiction',
    color: 'rose',
    icon: 'BookOpen',
    description: 'Understanding text types, purpose, audience, language, style, context and core grammar.',
    topics: [
      makeTopic({
        id: 'eng-1-1',
        slug: 'different-types-of-text',
        title: 'Different Types of Text',
        summary: 'Recognise fiction, non-fiction and common text forms.'
      }),
      makeTopic({
        id: 'eng-1-2',
        slug: 'purpose-and-audience',
        title: 'Purpose and Audience',
        summary: 'Understand why a text was written and who it is aimed at.'
      }),
      makeTopic({
        id: 'eng-1-3',
        slug: 'layout',
        title: 'Layout',
        summary: 'Explore how presentation and visual structure affect reading.'
      }),
      makeTopic({
        id: 'eng-1-4',
        slug: 'language-and-inference',
        title: 'Language and Inference',
        summary: 'Use word clues, connotations and tone to read between the lines and explain what a writer implies.'
      }),
      makeTopic({
        id: 'eng-1-5',
        slug: 'tone-register-and-style',
        title: 'Tone, Register and Style',
        summary: 'Understand how writers shape voice and effect.'
      }),
      makeTopic({
        id: 'eng-1-6',
        slug: 'informal-style',
        title: 'Informal Style',
        summary: 'Recognise and use features of informal writing.'
      }),
      makeTopic({
        id: 'eng-1-7',
        slug: 'formal-style',
        title: 'Formal Style',
        summary: 'Recognise and use features of formal writing.'
      }),
      makeTopic({
        id: 'eng-1-8',
        slug: 'figurative-language',
        title: 'Figurative Language',
        summary: 'Explore similes, metaphors and imagery.'
      }),
      makeTopic({
        id: 'eng-1-9',
        slug: 'oxymorons-irony-and-sarcasm',
        title: 'Oxymorons, Irony and Sarcasm',
        summary: 'Understand subtle and layered language effects.'
      }),
      makeTopic({
        id: 'eng-1-10',
        slug: 'fact-opinion-lists-of-three-and-rhetorical-questions',
        title: 'Fact, Opinion, Lists of Three and Rhetorical Questions',
        summary: 'Identify persuasive and informative language methods.'
      }),
      makeTopic({
        id: 'eng-1-11',
        slug: 'narrative',
        title: 'Narrative',
        summary: 'Understand how stories are shaped and told.'
      }),
      makeTopic({
        id: 'eng-1-12',
        slug: 'context',
        title: 'Context',
        summary: 'Explore how background knowledge shapes meaning.'
      }),
      makeTopic({
        id: 'eng-1-13',
        slug: 'grammar-and-punctuation',
        title: 'Grammar and Punctuation',
        summary: 'Build sentence accuracy and control.'
      }),
      makeTopic({
        id: 'eng-1-14',
        slug: 'warm-up-and-worked-practice-questions-fiction-non-fiction',
        title: 'Warm-Up and Worked Practice Questions',
        summary: 'Scaffolded practice for Section 1.'
      }),
      makeTopic({
        id: 'eng-1-15',
        slug: 'worked-practice-questions-fiction-non-fiction',
        title: 'Worked Practice Questions',
        summary: 'Modelled responses for Section 1.'
      }),
      makeTopic({
        id: 'eng-1-16',
        slug: 'practice-questions-fiction-non-fiction',
        title: 'Practice Questions',
        summary: 'Independent practice for Section 1.'
      }),
      makeTopic({
        id: 'eng-1-17',
        slug: 'understanding-fiction-and-non-fiction-knowledge-organiser',
        title: 'Understanding Fiction & Non-Fiction — Knowledge Organiser',
        summary: 'Key knowledge summary for Section 1.'
      })
    ]
  },

  {
    id: 2,
    title: 'Reading & Writing Non-Fiction',
    slug: 'reading-and-writing-non-fiction',
    color: 'amber',
    icon: 'Newspaper',
    description: 'Reading, analysing and writing non-fiction forms.',
    topics: [
      makeTopic({
        id: 'eng-2-1',
        slug: 'layout-form-and-structure',
        title: 'Layout, Form and Structure',
        summary: 'Understand how non-fiction texts are organised and presented.'
      }),
      makeTopic({
        id: 'eng-2-2',
        slug: 'language-in-non-fiction-texts',
        title: 'Language in Non-Fiction Texts',
        summary: 'Explore persuasive, informative and transactional language.'
      }),
      makeTopic({
        id: 'eng-2-3',
        slug: 'answering-questions-about-non-fiction',
        title: 'Answering Questions About Non-Fiction',
        summary: 'Develop exam-style reading skills for non-fiction.'
      }),
      makeTopic({
        id: 'eng-2-4',
        slug: 'writing-letters',
        title: 'Writing Letters',
        summary: 'Learn the conventions of letter writing.'
      }),
      makeTopic({
        id: 'eng-2-5',
        slug: 'formal-letters',
        title: 'Formal Letters',
        summary: 'Write with structure, clarity and appropriate register.'
      }),
      makeTopic({
        id: 'eng-2-6',
        slug: 'informal-letters',
        title: 'Informal Letters',
        summary: 'Write naturally and effectively for personal audiences.'
      }),
      makeTopic({
        id: 'eng-2-7',
        slug: 'writing-to-persuade-and-argue',
        title: 'Writing to Persuade and Argue',
        summary: 'Construct convincing arguments and persuasive pieces.'
      }),
      makeTopic({
        id: 'eng-2-8',
        slug: 'writing-to-inform-advise-and-explain',
        title: 'Writing to Inform, Advise and Explain',
        summary: 'Communicate clearly for different non-fiction purposes.'
      }),
      makeTopic({
        id: 'eng-2-9',
        slug: 'warm-up-and-worked-practice-questions-non-fiction',
        title: 'Warm-Up and Worked Practice Questions',
        summary: 'Scaffolded practice for Section 2.'
      }),
      makeTopic({
        id: 'eng-2-10',
        slug: 'worked-practice-questions-non-fiction',
        title: 'Worked Practice Questions',
        summary: 'Modelled responses for Section 2.'
      }),
      makeTopic({
        id: 'eng-2-11',
        slug: 'practice-questions-non-fiction',
        title: 'Practice Questions',
        summary: 'Independent practice for Section 2.'
      }),
      makeTopic({
        id: 'eng-2-12',
        slug: 'reading-and-writing-non-fiction-knowledge-organiser',
        title: 'Reading & Writing Non-Fiction — Knowledge Organiser',
        summary: 'Key knowledge summary for Section 2.'
      })
    ]
  },

  {
    id: 3,
    title: 'Reading & Writing Fiction',
    slug: 'reading-and-writing-fiction',
    color: 'emerald',
    icon: 'Feather',
    description: 'Reading and writing stories, novels, poetry and plays.',
    topics: [
      makeTopic({
        id: 'eng-3-1',
        slug: 'setting-and-theme',
        title: 'Setting and Theme',
        summary: 'Explore place, atmosphere and key ideas in fiction.'
      }),
      makeTopic({
        id: 'eng-3-2',
        slug: 'characterisation',
        title: 'Characterisation',
        summary: 'Analyse how writers create memorable characters.'
      }),
      makeTopic({
        id: 'eng-3-3',
        slug: 'language-in-fiction-texts',
        title: 'Language in Fiction Texts',
        summary: 'Study language choices and their effects in fiction.'
      }),
      makeTopic({
        id: 'eng-3-4',
        slug: 'mood',
        title: 'Mood',
        summary: 'Understand how writers create emotional atmosphere.'
      }),
      makeTopic({
        id: 'eng-3-5',
        slug: 'characters-in-novels',
        title: 'Characters in Novels',
        summary: 'Analyse major and minor characters in prose fiction.'
      }),
      makeTopic({
        id: 'eng-3-6',
        slug: 'structure-of-novels',
        title: 'Structure of Novels',
        summary: 'Explore plot, sequencing and narrative organisation.'
      }),
      makeTopic({
        id: 'eng-3-7',
        slug: 'narrative-in-novels',
        title: 'Narrative in Novels',
        summary: 'Understand viewpoint, voice and storytelling methods.'
      }),
      makeTopic({
        id: 'eng-3-8',
        slug: 'answering-questions-about-novels',
        title: 'Answering Questions About Novels',
        summary: 'Build fiction reading responses for prose texts.'
      }),
      makeTopic({
        id: 'eng-3-9',
        slug: 'writing-stories',
        title: 'Writing Stories',
        summary: 'Plan and write effective creative narratives.'
      }),
      makeTopic({
        id: 'eng-3-10',
        slug: 'poetry-features',
        title: 'Poetry Features',
        summary: 'Recognise key elements of poetry.'
      }),
      makeTopic({
        id: 'eng-3-11',
        slug: 'poetic-techniques',
        title: 'Poetic Techniques',
        summary: 'Analyse methods poets use to create meaning.'
      }),
      makeTopic({
        id: 'eng-3-12',
        slug: 'rhyme',
        title: 'Rhyme',
        summary: 'Understand rhyme and its effects.'
      }),
      makeTopic({
        id: 'eng-3-13',
        slug: 'rhythm',
        title: 'Rhythm',
        summary: 'Explore pace, beat and flow in poetry.'
      }),
      makeTopic({
        id: 'eng-3-14',
        slug: 'poetic-form-and-structure',
        title: 'Poetic Form and Structure',
        summary: 'Study how poems are shaped on the page.'
      }),
      makeTopic({
        id: 'eng-3-15',
        slug: 'answering-questions-about-poetry',
        title: 'Answering Questions About Poetry',
        summary: 'Develop analytical responses to poems.'
      }),
      makeTopic({
        id: 'eng-3-16',
        slug: 'play-features',
        title: 'Play Features',
        summary: 'Understand how drama works as a written and performed form.'
      }),
      makeTopic({
        id: 'eng-3-17',
        slug: 'characters-in-plays',
        title: 'Characters in Plays',
        summary: 'Analyse dramatic character presentation.'
      }),
      makeTopic({
        id: 'eng-3-18',
        slug: 'staging',
        title: 'Staging',
        summary: 'Explore how dramatic meaning is created in performance.'
      }),
      makeTopic({
        id: 'eng-3-19',
        slug: 'answering-questions-about-plays',
        title: 'Answering Questions About Plays',
        summary: 'Develop exam-style responses for drama texts.'
      }),
      makeTopic({
        id: 'eng-3-20',
        slug: 'writing-poetry-and-plays',
        title: 'Writing Poetry and Plays',
        summary: 'Create original imaginative writing in different forms.'
      }),
      makeTopic({
        id: 'eng-3-21',
        slug: 'warm-up-and-worked-practice-questions-fiction',
        title: 'Warm-Up and Worked Practice Questions',
        summary: 'Scaffolded practice for Section 3.'
      }),
      makeTopic({
        id: 'eng-3-22',
        slug: 'worked-practice-questions-fiction',
        title: 'Worked Practice Questions',
        summary: 'Modelled responses for Section 3.'
      }),
      makeTopic({
        id: 'eng-3-23',
        slug: 'practice-questions-fiction',
        title: 'Practice Questions',
        summary: 'Independent practice for Section 3.'
      }),
      makeTopic({
        id: 'eng-3-24',
        slug: 'reading-and-writing-fiction-knowledge-organiser',
        title: 'Reading & Writing Fiction — Knowledge Organiser',
        summary: 'Key knowledge summary for Section 3.'
      })
    ]
  },

  {
    id: 4,
    title: 'Shakespeare',
    slug: 'shakespeare',
    color: 'purple',
    icon: 'Crown',
    description: 'Understanding Shakespeare’s language, themes, characters and context.',
    topics: [
      makeTopic({
        id: 'eng-4-1',
        slug: 'background-information',
        title: 'Background Information',
        summary: 'Learn the context behind Shakespeare’s world and theatre.'
      }),
      makeTopic({
        id: 'eng-4-2',
        slug: 'shakespeares-language',
        title: "Shakespeare's Language",
        summary: 'Decode archaic words, meanings and phrasing.'
      }),
      makeTopic({
        id: 'eng-4-3',
        slug: 'shakespeares-sentence-structure',
        title: "Shakespeare's Sentence Structure",
        summary: 'Understand grammar, syntax and expression in Shakespeare.'
      }),
      makeTopic({
        id: 'eng-4-4',
        slug: 'shakespeares-techniques',
        title: "Shakespeare's Techniques",
        summary: 'Explore dramatic and literary methods used by Shakespeare.'
      }),
      makeTopic({
        id: 'eng-4-5',
        slug: 'shakespeare-structure',
        title: 'Structure',
        summary: 'Study plot and dramatic structure in Shakespeare texts.'
      }),
      makeTopic({
        id: 'eng-4-6',
        slug: 'performance',
        title: 'Performance',
        summary: 'Consider how Shakespeare’s plays work in live performance.'
      }),
      makeTopic({
        id: 'eng-4-7',
        slug: 'shakespeare-context',
        title: 'Context',
        summary: 'Link historical and cultural context to interpretation.'
      }),
      makeTopic({
        id: 'eng-4-8',
        slug: 'shakespeare-characters',
        title: 'Characters',
        summary: 'Analyse major Shakespearean characters.'
      }),
      makeTopic({
        id: 'eng-4-9',
        slug: 'theme-deception-and-disguise',
        title: 'Theme — Deception and Disguise',
        summary: 'Explore deception and disguise as recurring Shakespearean themes.'
      }),
      makeTopic({
        id: 'eng-4-10',
        slug: 'theme-love',
        title: 'Theme — Love',
        summary: 'Explore different presentations of love in Shakespeare.'
      }),
      makeTopic({
        id: 'eng-4-11',
        slug: 'theme-fate',
        title: 'Theme — Fate',
        summary: 'Examine fate, destiny and inevitability.'
      }),
      makeTopic({
        id: 'eng-4-12',
        slug: 'theme-the-supernatural',
        title: 'Theme — The Supernatural',
        summary: 'Study supernatural ideas and their dramatic effects.'
      }),
      makeTopic({
        id: 'eng-4-13',
        slug: 'answering-questions-about-shakespeare',
        title: 'Answering Questions About Shakespeare',
        summary: 'Develop analytical responses to Shakespeare texts.'
      }),
      makeTopic({
        id: 'eng-4-14',
        slug: 'warm-up-and-worked-practice-questions-shakespeare',
        title: 'Warm-Up and Worked Practice Questions',
        summary: 'Scaffolded practice for Section 4.'
      }),
      makeTopic({
        id: 'eng-4-15',
        slug: 'worked-practice-questions-shakespeare',
        title: 'Worked Practice Questions',
        summary: 'Modelled responses for Section 4.'
      }),
      makeTopic({
        id: 'eng-4-16',
        slug: 'practice-questions-shakespeare',
        title: 'Practice Questions',
        summary: 'Independent practice for Section 4.'
      }),
      makeTopic({
        id: 'eng-4-17',
        slug: 'shakespeare-knowledge-organisers',
        title: 'Shakespeare — Knowledge Organisers',
        summary: 'Key knowledge summary for Section 4.'
      })
    ]
  },

  {
    id: 5,
    title: 'Improving Your Writing',
    slug: 'improving-your-writing',
    color: 'indigo',
    icon: 'PencilLine',
    description: 'SPaG, sentence control, paragraphing, essays and proofreading.',
    topics: [
      makeTopic({
        id: 'eng-5-1',
        slug: 'basic-punctuation',
        title: 'Basic Punctuation',
        summary: 'Use punctuation accurately and effectively.'
      }),
      makeTopic({
        id: 'eng-5-2',
        slug: 'sentences-phrases-and-clauses',
        title: 'Sentences, Phrases and Clauses',
        summary: 'Understand the building blocks of sentence construction.'
      }),
      makeTopic({
        id: 'eng-5-3',
        slug: 'commas',
        title: 'Commas',
        summary: 'Use commas with confidence and clarity.'
      }),
      makeTopic({
        id: 'eng-5-4',
        slug: 'colons',
        title: 'Colons',
        summary: 'Use colons accurately for explanation and emphasis.'
      }),
      makeTopic({
        id: 'eng-5-5',
        slug: 'semicolons',
        title: 'Semicolons',
        summary: 'Link related clauses and structure ideas precisely.'
      }),
      makeTopic({
        id: 'eng-5-6',
        slug: 'apostrophes',
        title: 'Apostrophes',
        summary: 'Use apostrophes for possession and omission.'
      }),
      makeTopic({
        id: 'eng-5-7',
        slug: 'inverted-commas',
        title: 'Inverted Commas',
        summary: 'Punctuate direct speech accurately.'
      }),
      makeTopic({
        id: 'eng-5-8',
        slug: 'brackets-dashes-and-hyphens',
        title: 'Brackets, Dashes and Hyphens',
        summary: 'Use parenthesis and linking punctuation effectively.'
      }),
      makeTopic({
        id: 'eng-5-9',
        slug: 'tenses',
        title: 'Tenses',
        summary: 'Keep tense choices accurate and controlled.'
      }),
      makeTopic({
        id: 'eng-5-10',
        slug: 'standard-english',
        title: 'Standard English',
        summary: 'Write accurately for formal contexts.'
      }),
      makeTopic({
        id: 'eng-5-11',
        slug: 'paragraphs',
        title: 'Paragraphs',
        summary: 'Understand the role of paragraphing in writing.'
      }),
      makeTopic({
        id: 'eng-5-12',
        slug: 'using-paragraphs',
        title: 'Using Paragraphs',
        summary: 'Organise ideas clearly through paragraphing.'
      }),
      makeTopic({
        id: 'eng-5-13',
        slug: 'linking-paragraphs',
        title: 'Linking Paragraphs',
        summary: 'Create smooth transitions between ideas.'
      }),
      makeTopic({
        id: 'eng-5-14',
        slug: 'making-your-writing-interesting',
        title: 'Making Your Writing Interesting',
        summary: 'Develop engaging and varied writing.'
      }),
      makeTopic({
        id: 'eng-5-15',
        slug: 'using-adjectives-and-comparisons',
        title: 'Using Adjectives and Comparisons',
        summary: 'Add detail and comparison effectively.'
      }),
      makeTopic({
        id: 'eng-5-16',
        slug: 'using-impressive-vocabulary',
        title: 'Using Impressive Vocabulary',
        summary: 'Choose ambitious words with precision.'
      }),
      makeTopic({
        id: 'eng-5-17',
        slug: 'varying-sentence-length',
        title: 'Varying Sentence Length',
        summary: 'Control pace and emphasis through sentence variation.'
      }),
      makeTopic({
        id: 'eng-5-18',
        slug: 'essays-the-basics',
        title: 'Essays: The Basics',
        summary: 'Learn the foundations of effective essay writing.'
      }),
      makeTopic({
        id: 'eng-5-19',
        slug: 'planning-essays',
        title: 'Planning Essays',
        summary: 'Plan ideas before writing analytical responses.'
      }),
      makeTopic({
        id: 'eng-5-20',
        slug: 'writing-essays',
        title: 'Writing Essays',
        summary: 'Write structured, clear and developed essays.'
      }),
      makeTopic({
        id: 'eng-5-21',
        slug: 'structuring-essays',
        title: 'Structuring Essays',
        summary: 'Shape essays so arguments build clearly.'
      }),
      makeTopic({
        id: 'eng-5-22',
        slug: 'drafting-and-editing-essays',
        title: 'Drafting and Editing Essays',
        summary: 'Improve essays through revision and redrafting.'
      }),
      makeTopic({
        id: 'eng-5-23',
        slug: 'using-examples',
        title: 'Using Examples',
        summary: 'Support points with effective examples.'
      }),
      makeTopic({
        id: 'eng-5-24',
        slug: 'using-quotes',
        title: 'Using Quotes',
        summary: 'Embed quotations accurately and purposefully.'
      }),
      makeTopic({
        id: 'eng-5-25',
        slug: 'explaining-quotes',
        title: 'Explaining Quotes',
        summary: 'Move beyond spotting quotes to real analysis.'
      }),
      makeTopic({
        id: 'eng-5-26',
        slug: 'comparing-texts',
        title: 'Comparing Texts',
        summary: 'Write comparisons across ideas, methods and viewpoints.'
      }),
      makeTopic({
        id: 'eng-5-27',
        slug: 'evaluating-texts',
        title: 'Evaluating Texts',
        summary: 'Judge effectiveness and writer choices critically.'
      }),
      makeTopic({
        id: 'eng-5-28',
        slug: 'proofreading-your-writing',
        title: 'Proofreading Your Writing',
        summary: 'Check and refine technical accuracy.'
      }),
      makeTopic({
        id: 'eng-5-29',
        slug: 'warm-up-and-worked-practice-questions-improving-writing',
        title: 'Warm-Up and Worked Practice Questions',
        summary: 'Scaffolded practice for Section 5.'
      }),
      makeTopic({
        id: 'eng-5-30',
        slug: 'practice-questions-improving-writing',
        title: 'Practice Questions',
        summary: 'Independent practice for Section 5.'
      }),
      makeTopic({
        id: 'eng-5-31',
        slug: 'spelling-punctuation-and-grammar-knowledge-organiser',
        title: 'Spelling, Punctuation and Grammar — Knowledge Organiser',
        summary: 'Key SPaG knowledge summary.'
      }),
      makeTopic({
        id: 'eng-5-32',
        slug: 'improving-your-writing-knowledge-organiser',
        title: 'Improving Your Writing — Knowledge Organiser',
        summary: 'Key knowledge summary for Section 5.'
      })
    ]
  },

  {
    id: 6,
    title: 'Spoken Language',
    slug: 'spoken-language',
    color: 'cyan',
    icon: 'Microphone',
    description: 'Speaking, listening, presenting and performing.',
    topics: [
      makeTopic({
        id: 'eng-6-1',
        slug: 'spoken-language-tasks',
        title: 'Spoken Language Tasks',
        summary: 'Understand the demands of spoken language activities.'
      }),
      makeTopic({
        id: 'eng-6-2',
        slug: 'speeches-and-presentations',
        title: 'Speeches and Presentations',
        summary: 'Plan and deliver effective spoken presentations.'
      }),
      makeTopic({
        id: 'eng-6-3',
        slug: 'performing-plays-and-poems',
        title: 'Performing Plays and Poems',
        summary: 'Develop confidence and interpretation in performance.'
      }),
      makeTopic({
        id: 'eng-6-4',
        slug: 'using-language-correctly',
        title: 'Using Language Correctly',
        summary: 'Use accurate spoken standard English when needed.'
      }),
      makeTopic({
        id: 'eng-6-5',
        slug: 'spoken-language-knowledge-organiser',
        title: 'Spoken Language — Knowledge Organiser',
        summary: 'Key knowledge summary for Section 6.'
      })
    ]
  },

  {
    id: 7,
    title: 'Exam Practice',
    slug: 'exam-practice',
    color: 'orange',
    icon: 'Exam',
    description: 'Mixed tests and workbook-aligned English practice papers.',
    topics: [
      makeTopic({
        id: 'eng-7-1',
        slug: 'mixed-practice-tests',
        title: 'Mixed Practice Tests',
        summary: 'Mixed review tasks across the course.'
      }),
      makeTopic({
        id: 'eng-7-2',
        slug: 'english-language-practice-paper-1-fiction',
        title: 'English Language Practice Paper 1: Fiction',
        summary: 'Fiction reading and writing practice paper.'
      }),
      makeTopic({
        id: 'eng-7-3',
        slug: 'english-language-practice-paper-2-non-fiction',
        title: 'English Language Practice Paper 2: Non-Fiction',
        summary: 'Non-fiction reading and writing practice paper.'
      })
      // Literature exam-practice topics (Unseen Poetry, Shakespeare, 19th-Century Prose,
      // Modern Texts & Anthology Poetry, Final Exam Technique) are authored directly in
      // englishcontent7.js and appended below — no placeholder needed here.
    ]
  }
];

// ============================================================
// Adapter helpers
// ============================================================

const AUTHOR_SECTIONS = [
  {
    slug: 'understanding-fiction-and-non-fiction',
    content: englishContent1
  },
  {
    slug: 'reading-and-writing-non-fiction',
    content: englishContent2
  },
  {
    slug: 'reading-and-writing-fiction',
    content: englishContent3
  },
  {
    slug: 'shakespeare',
    content: englishContent4
  },
  {
    slug: 'improving-your-writing',
    content: englishContent5
  },
  {
    slug: 'spoken-language',
    content: englishContent6
  },
  {
    slug: 'exam-practice',
    content: englishContent7
  }
];

const ENGLISH_SECTION_SLUG_ALIASES = {
  'fiction-nonfiction': 'understanding-fiction-and-non-fiction',
  'fiction-non-fiction': 'understanding-fiction-and-non-fiction',
  'reading-writing-nonfiction': 'reading-and-writing-non-fiction'
};

const AUTHOR_TOPIC_SLUG_ALIASES = new Map([
  ['warm-up-and-worked-practice-questions-fiction-nonfiction', 'warm-up-and-worked-practice-questions-fiction-non-fiction'],
  ['worked-practice-questions-fiction-nonfiction', 'worked-practice-questions-fiction-non-fiction'],
  ['practice-questions-fiction-nonfiction', 'practice-questions-fiction-non-fiction']
]);

export function resolveEnglishSectionSlug(slug) {
  if (!slug || typeof slug !== 'string') return slug;
  return ENGLISH_SECTION_SLUG_ALIASES[slug.trim()] || slug.trim();
}

export function getEnglishSection(slug) {
  const canonicalSlug = resolveEnglishSectionSlug(slug);
  return englishSections.find((section) => section.slug === canonicalSlug) || null;
}

function resolveAuthorTopicSlug(slug) {
  if (!slug || typeof slug !== 'string') return slug;
  return AUTHOR_TOPIC_SLUG_ALIASES.get(slug.trim()) || slug.trim();
}

const normaliseText = (value = '') =>
  String(value || '')
    .replace(/\r/g, '')
    .trim();

const lowerClean = (value = '') =>
  normaliseText(value)
    .toLowerCase()
    .replace(/\s+/g, ' ');

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

function splitExplainerContent(content) {
  const raw = normaliseText(content);

  if (!raw) {
    return {
      paragraphs: [],
      bulletLines: [],
      proseParas: []
    };
  }

  const paragraphs = raw
    .split(/\n\s*\n/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean);

  const bulletLines = raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => /^[-•*]\s+/.test(line))
    .map(line => line.replace(/^[-•*]\s+/, '').trim())
    .filter(Boolean);

  const proseParas = paragraphs.filter(paragraph => {
    const lines = paragraph
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    return !lines.length || !lines.every(line => /^[-•*]\s+/.test(line));
  });

  return {
    paragraphs,
    bulletLines,
    proseParas
  };
}

function extractModelAnswer(content) {
  const raw = normaliseText(content);

  if (!raw) {
    return {
      prompt: '',
      modelAnswer: ''
    };
  }

  const parts = raw.split(/\n\s*Model answer:\s*\n?/i);

  return {
    prompt: normaliseText(parts[0]),
    modelAnswer: normaliseText(parts.slice(1).join('\n\n'))
  };
}

function normaliseQuestionType(type) {
  const raw = lowerClean(type);

  if (raw === 'identify') return 'mcq';
  if (raw === 'multiple-choice') return 'mcq';
  if (raw === 'multiple choice') return 'mcq';
  if (raw === 'truefalse') return 'true-false';
  if (raw === 'true false') return 'true-false';
  if (raw === 'true_false') return 'true-false';
  if (raw === 'short-answer') return 'short';
  if (raw === 'short answer') return 'short';

  return raw || 'mcq';
}

function parseBooleanAnswer(value) {
  if (typeof value === 'boolean') return value;

  const cleaned = lowerClean(value);

  if (cleaned === 'true') return true;
  if (cleaned === 'yes') return true;
  if (cleaned === 'correct') return true;
  if (cleaned === 'false') return false;
  if (cleaned === 'no') return false;
  if (cleaned === 'incorrect') return false;

  return Boolean(value);
}

function convertInteractiveStep(step) {
  if (!step || step.type !== 'question') return null;

  const type = normaliseQuestionType(
    step.questionType ||
    step.answerType ||
    step.inputType ||
    'mcq'
  );

  const q = normaliseText(step.question || step.q);

  if (!q) return null;

  if (type === 'true-false') {
    return {
      q,
      type,
      answer: parseBooleanAnswer(step.correct ?? step.answer),
      feedback: normaliseText(step.explanation || step.feedback)
    };
  }

  if (type === 'short') {
    return {
      q,
      type,
      answers: toArray(step.answers || step.correct || step.answer)
        .map(answer => lowerClean(answer))
        .filter(Boolean),
      feedback: normaliseText(step.explanation || step.feedback),
      hint: normaliseText(step.hint)
    };
  }

  return {
    q,
    type: 'mcq',
    options: toArray(step.options),
    answer:
      typeof step.correct === 'number'
        ? step.correct
        : typeof step.answer === 'number'
          ? step.answer
          : 0,
    feedback: normaliseText(step.explanation || step.feedback)
  };
}

function convertPracticeQuestion(question) {
  if (!question) return null;

  const type = normaliseQuestionType(question.type);
  const q = normaliseText(question.question || question.q);

  if (!q) return null;

  if (type === 'true-false') {
    return {
      q,
      type,
      answer: parseBooleanAnswer(question.correct ?? question.answer),
      feedback: normaliseText(question.feedback || question.explanation)
    };
  }

  if (type === 'short') {
    return {
      q,
      type,
      answers: toArray(question.answers || question.correct || question.answer)
        .map(answer => lowerClean(answer))
        .filter(Boolean),
      hint: normaliseText(question.hint),
      feedback: normaliseText(question.feedback || question.explanation)
    };
  }

  return {
    q,
    type: 'mcq',
    options: toArray(question.options),
    answer:
      typeof question.correct === 'number'
        ? question.correct
        : typeof question.answer === 'number'
          ? question.answer
          : 0,
    feedback: normaliseText(question.feedback || question.explanation)
  };
}

function ensureMinimumPractice(practice, interactive, minimum = 5) {
  const output = [...practice];
  const existingQuestions = new Set(output.map(item => item.q));

  for (const item of interactive) {
    if (output.length >= minimum) break;
    if (!item?.q || existingQuestions.has(item.q)) continue;

    if (item.type === 'short') {
      output.push({
        q: item.q,
        type: 'short',
        answers: item.answers || [],
        hint: item.hint || ''
      });
    } else if (item.type === 'true-false') {
      output.push({
        q: item.q,
        type: 'true-false',
        answer: Boolean(item.answer)
      });
    } else {
      output.push({
        q: item.q,
        type: 'mcq',
        options: item.options || [],
        answer: item.answer ?? 0
      });
    }

    existingQuestions.add(item.q);
  }

  return output;
}

// ============================================================
// Worked example builders
// ============================================================

function buildWorkedExampleFromContent(content, fallbackTitle) {
  const { prompt, modelAnswer } = extractModelAnswer(content);

  const safeTitle = normaliseText(fallbackTitle) || 'this topic';

  const safePrompt =
    prompt ||
    `Worked example for ${safeTitle}.`;

  const goodAnswer =
    modelAnswer ||
    `This response explains ${safeTitle.toLowerCase()} clearly and gives a relevant reason.`;

  return {
    label: 'Example 1 — Modelled answer',
    prompt: safePrompt,
    tiers: [
      {
        label: 'Weak',
        text: 'It is correct because it is about the topic.',
        critique:
          'This is too vague. It gives a basic answer but does not use evidence, terminology or explanation.'
      },
      {
        label: 'Good',
        text: goodAnswer,
        critique:
          'This gives a clear answer and explains the main idea. To improve further, it could use more precise evidence or terminology.'
      },
      {
        label: 'Excellent',
        text:
          modelAnswer ||
          `A precise response on ${safeTitle.toLowerCase()} would use evidence, explain the effect on the reader, and apply accurate subject terminology throughout.`,
        critique:
          'This is more developed because it links the answer to evidence, effect and subject vocabulary.'
      }
    ]
  };
}

function buildWorkedExampleFromApplication(topic) {
  const prompt = normaliseText(topic.applicationTask?.prompt);

  if (!prompt) return null;

  const title = normaliseText(topic.title) || 'this topic';
  const lowerTitle = title.toLowerCase();

  const criteria = toArray(topic.aiFeedback?.successCriteria)
    .map(normaliseText)
    .filter(Boolean);

  const hints = toArray(topic.applicationTask?.hints)
    .map(normaliseText)
    .filter(Boolean);

  const criteriaSentence = criteria.length
    ? `It meets the success criteria by ${criteria.slice(0, 3).join(', ').toLowerCase()}.`
    : '';

  const hintSentence = hints.length
    ? `It follows the guidance by ${hints.slice(0, 2).join(' and ').toLowerCase()}.`
    : '';

  return {
    label: 'Example 2 — Application task',
    prompt,
    tiers: [
      {
        label: 'Weak',
        text: `This answer is about ${lowerTitle}. It gives a simple idea but does not explain much.`,
        critique:
          'This shows basic understanding but needs evidence, development and more precise vocabulary.'
      },
      {
        label: 'Good',
        text: `This answer addresses ${lowerTitle}, gives one clear point, and explains the main idea using relevant detail.`,
        critique:
          'This is clearer because it responds directly to the task and gives a reason.'
      },
      {
        label: 'Excellent',
        text: [
          `This is a precise, developed answer on ${lowerTitle}.`,
          'It uses relevant evidence, explains the effect on the reader or audience, and applies subject-specific vocabulary confidently.',
          criteriaSentence,
          hintSentence
        ]
          .filter(Boolean)
          .join(' '),
        critique:
          'This is strongest because it is specific, evidence-based and clearly linked to the task.'
      }
    ]
  };
}

function normaliseAuthoredWorkedExamples(topic) {
  if (Array.isArray(topic.workedExamples) && topic.workedExamples.length) {
    return topic.workedExamples
      .map((example, index) => ({
        label: example.label || `Example ${index + 1}`,
        prompt: normaliseText(example.prompt),
        tiers: toArray(example.tiers)
          .map(tier => ({
            label: normaliseText(tier.label),
            text: normaliseText(tier.text),
            critique: normaliseText(tier.critique)
          }))
          .filter(tier => tier.label || tier.text || tier.critique)
      }))
      .filter(example => example.prompt || example.tiers.length);
  }

  const primary = buildWorkedExampleFromContent(
    topic.workedExample?.content,
    topic.title
  );

  const secondary = buildWorkedExampleFromApplication(topic);

  return [
    primary,
    ...(secondary ? [secondary] : [])
  ];
}

// ============================================================
// Main topic adapter
// ============================================================

function adaptAuthorTopic(topic) {
  const { bulletLines, proseParas } = splitExplainerContent(
    topic.explainer?.content
  );

  const title = normaliseText(topic.title) || 'Untitled English Topic';

  const intro =
    proseParas[0] ||
    normaliseText(topic.summary) ||
    `This lesson introduces ${title}.`;

  const keyPoints =
    bulletLines.length > 0
      ? bulletLines
      : proseParas.slice(1, 7);

  const examples = proseParas.slice(2, 5).map((paragraph, index) => ({
    label: `Example ${index + 1}`,
    text: paragraph
  }));

  const interactive = toArray(topic.interactiveLesson?.steps)
    .map(convertInteractiveStep)
    .filter(item => item && item.q);

  const practiceRaw = toArray(topic.practice)
    .map(convertPracticeQuestion)
    .filter(item => item && item.q);

  const practice = ensureMinimumPractice(practiceRaw, interactive, 5);

  const workedExamples = normaliseAuthoredWorkedExamples(topic);
  const primaryWorkedExample =
    workedExamples[0] ||
    buildWorkedExampleFromContent('', title);

  const applicationHints = toArray(topic.applicationTask?.hints)
    .map(normaliseText)
    .filter(Boolean);

  const successCriteria = toArray(topic.aiFeedback?.successCriteria)
    .map(normaliseText)
    .filter(Boolean);

  return {
    id: topic.id,
    slug: topic.slug,
    title,
    summary: normaliseText(topic.summary),
    yearGroup: topic.yearGroup || '7-11',

    explainer: {
      intro,
      keyPoints: keyPoints.length
        ? keyPoints
        : [
            `Understand the key ideas in ${title}.`,
            'Use evidence carefully.',
            'Explain the effect on the reader or audience.'
          ],
      rule: {
        name: normaliseText(topic.explainer?.title) || title,
        description:
          proseParas[1] ||
          proseParas[0] ||
          normaliseText(topic.summary) ||
          `Key guidance for ${title}.`
      },
      examples: examples.length
        ? examples
        : [
            {
              label: 'Example',
              text: `A strong answer on ${title.toLowerCase()} should use clear evidence and explain its effect.`
            }
          ]
    },

    interactive: interactive.length
      ? interactive
      : [
          {
            q: `Quick check: what is the main focus of ${title}?`,
            type: 'mcq',
            options: [
              'Understanding and applying the skill',
              'Guessing without evidence',
              'Ignoring the text',
              'Only copying the title'
            ],
            answer: 0,
            feedback:
              'Correct — the aim is to understand the skill and apply it accurately.'
          }
        ],

    workedExample: {
      prompt: primaryWorkedExample.prompt,
      tiers: primaryWorkedExample.tiers
    },

    workedExamples,

    practice: practice.length
      ? practice
      : [
          {
            q: `Practice check for ${title}. What should a strong answer include?`,
            type: 'mcq',
            options: [
              'A clear point, evidence and explanation',
              'Only one word',
              'No evidence',
              'A random guess'
            ],
            answer: 0
          }
        ],

    application: {
      prompt:
        normaliseText(topic.applicationTask?.prompt) ||
        `Write a short response applying what you have learned about ${title}.`,
      guidance:
        applicationHints.join(' ') ||
        `Use what you have learned about ${title.toLowerCase()} to answer clearly.`,
      wordTarget: topic.applicationTask?.wordTarget || '60-120',
      techniques: successCriteria.length
        ? successCriteria
        : [
            'Use clear evidence',
            'Explain your reasoning',
            'Use accurate subject vocabulary'
          ]
    },

    improvement: {
      lead:
        normaliseText(topic.improvementTask?.prompt) ||
        'Rewrite your answer, improving the areas highlighted in your feedback.'
    },

    isAuthored: true,
    mastery: topic.mastery || null,
    aiFeedback: topic.aiFeedback || null
  };
}

// ============================================================
// Section merger
// ============================================================

function mergeAuthoredSection(targetSection, authoredSection) {
  if (!targetSection || !authoredSection || !Array.isArray(authoredSection.topics)) {
    return targetSection;
  }

  const adaptedBySlug = new Map();

  authoredSection.topics.forEach(topic => {
    if (!topic?.slug) return;
    const canonicalSlug = resolveAuthorTopicSlug(topic.slug);
    adaptedBySlug.set(canonicalSlug, adaptAuthorTopic(topic));
  });

  const existingSlugs = new Set();

  const mergedTopics = targetSection.topics.map(existingTopic => {
    existingSlugs.add(existingTopic.slug);

    const adapted = adaptedBySlug.get(existingTopic.slug);

    if (!adapted) return existingTopic;

    return {
      ...adapted,
      // Preserve existing IDs so saved user progress remains linked.
      id: existingTopic.id || adapted.id
    };
  });

  adaptedBySlug.forEach((adaptedTopic, slug) => {
    if (!existingSlugs.has(slug)) {
      mergedTopics.push(adaptedTopic);
    }
  });

  return {
    ...targetSection,
    topics: mergedTopics
  };
}

// ============================================================
// Merge authored workbook content into base sections
// ============================================================

AUTHOR_SECTIONS.forEach(({ slug, content }) => {
  const index = englishSections.findIndex(section => section.slug === slug);

  if (index === -1) return;

  englishSections[index] = mergeAuthoredSection(englishSections[index], content);
});

// ============================================================
// Append GCSE-aligned Y10/Y11 modules
// ============================================================

const gcseSections = [
  ...toArray(englishGCSESections),
  ...toArray(englishGCSESections2)
].filter(section => section && section.slug && Array.isArray(section.topics));

const existingSectionSlugs = new Set(
  englishSections.map(section => section.slug)
);

gcseSections.forEach(section => {
  if (!existingSectionSlugs.has(section.slug)) {
    englishSections.push(section);
    existingSectionSlugs.add(section.slug);
  }
});

// ============================================================
// Decorate topics with examiner-bank insights
// ============================================================

englishSections.forEach(section => {
  section.topics = attachExaminerInsights(section.topics || []);
});

// ============================================================
// Helpers
// ============================================================

export const getTotalEnglishTopics = () =>
  englishSections.reduce(
    (acc, section) => acc + (section.topics?.length || 0),
    0
  );

export const findEnglishTopic = (sectionSlug, topicSlug) => {
  const section = getEnglishSection(sectionSlug);

  if (!section) {
    return {
      section: null,
      topic: null
    };
  }

  const topic = section.topics.find(item => item.slug === topicSlug);

  return {
    section,
    topic: topic || null
  };
};

export default englishSections;